import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Grid, GridColumn, GridCustomCellProps } from "@progress/kendo-react-grid";
import { pencilIcon, trashIcon, folderIcon } from "@progress/kendo-svg-icons";
import { useInjection } from "@presentation/components/dependencyInjection";
import { DeploymentConfigurationService } from "@application/useCases/workspace/DeploymentConfigurationService";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { useNotifications } from "@presentation/components/notifications";
import { EditStoragePathDialog } from "@presentation/components/storageConfiguration/EditStoragePathDialog";
import { FolderPickerDialog, RESULTS_PATH_BROWSE_PATH_TYPE } from "@presentation/components/storageConfiguration/FolderPickerDialog";
import { isValidStoragePath, STORAGE_PATH_HELP_TEXT } from "@presentation/components/storageConfiguration/storagePathValidation";
import type { PathTypeSchema, PathVisibilitySchema, StorageContextConfiguration } from "@domain/schemas/BI";

// `BI.schema.d.ts` enums have no runtime object under Vitest (ambient/type-only in a .d.ts file;
// Vite's esbuild transpiles it anyway, so production is unaffected) - string literals cast to the
// enum type sidestep that, matching this codebase's established workaround.
const VISIBILITY_SHARED = "Shared" as PathVisibilitySchema;
const VISIBILITY_PRIVATE = "Private" as PathVisibilitySchema;
const FACETS = ["Raw", "Fasta", "SoftwareSettings", "Libraries"] as PathTypeSchema[];

/**
Deployment-level storage paths and results path (Admin-only) - the root of the team/project
storage-configuration fallback chain. Unlike the team/project tiers, a deployment path's visibility
is fixed at creation (no toggle endpoint exists), and there is no "effective" view to resolve against.
*/
export const DeploymentStorageConfigurationSection: React.FC = () => {
  const deploymentConfigurationService = useInjection(DeploymentConfigurationService);
  const { pushNotification } = useNotifications();
  const [configuration, setConfiguration] = useState<StorageContextConfiguration | null>(null);
  const [newPathByFacet, setNewPathByFacet] = useState<Partial<Record<PathTypeSchema, string>>>({});
  const [newVisibilityByFacet, setNewVisibilityByFacet] = useState<Partial<Record<PathTypeSchema, PathVisibilitySchema>>>({});
  const [resultsPath, setResultsPath] = useState("");
  const [editingPath, setEditingPath] = useState<{ id: string; path: string; pathType: PathTypeSchema } | null>(null);
  const [browsingFacet, setBrowsingFacet] = useState<PathTypeSchema | null>(null);
  const [browsingResultsPath, setBrowsingResultsPath] = useState(false);

  const reload = useCallback(async () => {
    const own = await deploymentConfigurationService.getStorageConfigurationAsync();
    setConfiguration(own);
    setResultsPath(own.resultsPath ?? "");
  }, [deploymentConfigurationService]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /* eslint-disable unicorn/no-declarations-before-early-exit -- every handler below is used by the JSX returned after the early return below */
  const handleAddPath = async (pathType: PathTypeSchema) => {
    const path = (newPathByFacet[pathType] ?? "").trim();
    if (!path) return;
    if (!isValidStoragePath(path)) {
      pushNotification(STORAGE_PATH_HELP_TEXT, "error", 10_000);
      return;
    }
    try {
      const updated = await deploymentConfigurationService.addStoragePathAsync(pathType, path, newVisibilityByFacet[pathType] ?? VISIBILITY_SHARED);
      setConfiguration(updated);
      setNewPathByFacet((state) => ({ ...state, [pathType]: "" }));
    } catch (error) {
      pushNotification(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not add this path.", "error", 10_000);
    }
  };

  const handleRemovePath = async (pathId: string) => {
    try {
      setConfiguration(await deploymentConfigurationService.removeStoragePathAsync(pathId));
    } catch (error) {
      pushNotification(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not remove this path.", "error", 10_000);
    }
  };

  // Changes the path's value in place (no remove/re-add), so it never drops below the
  // at-least-one-row-per-facet floor this level enforces.
  const handleChangePath = async (pathId: string, newPath: string) => {
    setConfiguration(await deploymentConfigurationService.setStoragePathValueAsync(pathId, newPath));
  };

  const handleSaveResultsPath = async () => {
    const path = resultsPath.trim();
    if (!isValidStoragePath(path)) {
      pushNotification(STORAGE_PATH_HELP_TEXT, "error", 10_000);
      return;
    }
    try {
      setConfiguration(await deploymentConfigurationService.setStorageResultsPathAsync(path));
    } catch (error) {
      pushNotification(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not save the results path.", "error", 10_000);
    }
  };
  /* eslint-enable unicorn/no-declarations-before-early-exit */

  if (!configuration) return null;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gap: 8 }}>
        <strong>Results path</strong>
        <div style={{ display: "flex", gap: 8 }}>
          <Input placeholder="Results path" style={{ flex: 1 }} value={resultsPath} onChange={(event) => setResultsPath(String(event.value ?? ""))} />
          <Button type="button" disabled={!isValidStoragePath(resultsPath.trim())} onClick={() => void handleSaveResultsPath()}>
            Save
          </Button>
          <Button type="button" fillMode="flat" svgIcon={folderIcon} title="Browse results path" aria-label="Browse results path" onClick={() => setBrowsingResultsPath(true)} />
        </div>
        <span style={{ fontSize: 12, color: "#64748b" }}>{STORAGE_PATH_HELP_TEXT} May be left blank for the container/bucket root.</span>
      </div>

      {FACETS.map((facet) => {
        const paths = configuration.paths?.filter((path) => path.pathType === facet) ?? [];
        // A facet with nothing configured still renders as a single placeholder row ('-'), rather
        // than swapping the table out for plain text.
        const rows: Array<{ id: string; path: string; visibility: PathVisibilitySchema | null }> =
          paths.length === 0 ? [{ id: "empty", path: "-", visibility: null }] : paths.map((path) => ({ id: path.id!, path: path.path!, visibility: path.visibility! }));

        return (
          <div key={facet} style={{ display: "grid", gap: 8 }}>
            <strong>{facet}</strong>
            <Grid data={rows} dataItemKey="id">
              <GridColumn field="path" title="Path" />
              <GridColumn title="Visibility" width="120px" cell={(properties: GridCustomCellProps) => <td>{properties.dataItem.visibility ?? "-"}</td>} />
              <GridColumn
                title=""
                width="90px"
                cell={(properties: GridCustomCellProps) =>
                  properties.dataItem.id === "empty" ? (
                    <td />
                  ) : (
                    <td>
                      <div style={{ display: "flex", gap: 8 }}>
                        <Button
                          type="button"
                          fillMode="flat"
                          svgIcon={pencilIcon}
                          title="Change path"
                          aria-label="Change path"
                          onClick={() => setEditingPath({ id: properties.dataItem.id!, path: properties.dataItem.path, pathType: facet })}
                        />
                        <Button type="button" fillMode="flat" themeColor="error" svgIcon={trashIcon} title="Remove" aria-label="Remove" onClick={() => handleRemovePath(properties.dataItem.id!)} />
                      </div>
                    </td>
                  )
                }
              />
            </Grid>
            <div style={{ display: "flex", gap: 8 }}>
              <Input
                placeholder="Path"
                style={{ flex: 1, minWidth: 0 }}
                value={newPathByFacet[facet] ?? ""}
                onChange={(event) => setNewPathByFacet((state) => ({ ...state, [facet]: String(event.value ?? "") }))}
              />
              <DropDownList
                style={{ flex: "0 0 auto", width: 130 }}
                data={[VISIBILITY_SHARED, VISIBILITY_PRIVATE]}
                value={newVisibilityByFacet[facet] ?? VISIBILITY_SHARED}
                onChange={(event) => setNewVisibilityByFacet((state) => ({ ...state, [facet]: event.value as PathVisibilitySchema }))}
              />
              <Button type="button" disabled={!isValidStoragePath((newPathByFacet[facet] ?? "").trim())} onClick={() => void handleAddPath(facet)}>
                Add
              </Button>
              <Button type="button" fillMode="flat" svgIcon={folderIcon} title="Browse" aria-label="Browse" onClick={() => setBrowsingFacet(facet)} />
            </div>
            <span style={{ fontSize: 12, color: "#64748b" }}>{STORAGE_PATH_HELP_TEXT}</span>
          </div>
        );
      })}

      {editingPath && (
        <EditStoragePathDialog
          currentPath={editingPath.path}
          pathType={editingPath.pathType}
          listFoldersAsync={(pathType, folder) => deploymentConfigurationService.listFoldersAsync(pathType, folder)}
          onClose={() => setEditingPath(null)}
          onSubmit={(newPath) => handleChangePath(editingPath.id, newPath)}
        />
      )}
      {browsingFacet && (
        <FolderPickerDialog
          pathType={browsingFacet}
          initialFolder={newPathByFacet[browsingFacet] ?? ""}
          listFoldersAsync={(pathType, folder) => deploymentConfigurationService.listFoldersAsync(pathType, folder)}
          onSelect={(folder) => {
            setNewPathByFacet((state) => ({ ...state, [browsingFacet]: folder }));
            setBrowsingFacet(null);
          }}
          onClose={() => setBrowsingFacet(null)}
        />
      )}
      {browsingResultsPath && (
        <FolderPickerDialog
          pathType={RESULTS_PATH_BROWSE_PATH_TYPE}
          initialFolder={resultsPath}
          listFoldersAsync={(pathType, folder) => deploymentConfigurationService.listFoldersAsync(pathType, folder)}
          onSelect={(folder) => {
            setResultsPath(folder);
            setBrowsingResultsPath(false);
          }}
          onClose={() => setBrowsingResultsPath(false)}
        />
      )}
    </div>
  );
};

export default DeploymentStorageConfigurationSection;
